import React, { useState } from 'react';
import { ShieldAlert, Key, Smartphone, AlertCircle, CheckCircle } from 'lucide-react';

const Security = ({ userData }: { userData?: any }) => {
  const [passwords, setPasswords] = useState({ current: '', new: '' });
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null, msg: string }>({ type: null, msg: '' });
  const [isUpdating, setIsUpdating] = useState(false);

  if (!userData) return null;

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: null, msg: '' });

    if (!passwords.current || !passwords.new) {
      setStatus({ type: 'error', msg: 'Please fill all fields' });
      return;
    }

    if (passwords.new.length < 6) {
      setStatus({ type: 'error', msg: 'New password must be at least 6 characters' });
      return;
    }

    setIsUpdating(true);

    try {
      const response = await fetch(`http://localhost:10001/api/profile/${userData.id}/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: passwords.current,
          newPassword: passwords.new
        })
      });

      const result = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', msg: result.message });
        setPasswords({ current: '', new: '' }); // Form clear kar do success par
      } else {
        setStatus({ type: 'error', msg: result.error || 'Failed to update password' });
      }
    } catch (error) {
      setStatus({ type: 'error', msg: 'Server connection error' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white">Security Settings</h1>
        <p className="text-gray-400 mt-1">Keep your account safe and manage connected devices.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Change Password Card */}
        <div className="bg-[#131b2c] border border-gray-800 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center gap-3 mb-6">
            <Key className="text-blue-500" size={24} />
            <h3 className="text-xl font-bold text-white">Change Password</h3>
          </div>

          {/* Status Message Display */}
          {status.type && (
            <div className={`flex items-center gap-2 px-4 py-3 rounded-lg mb-6 text-sm font-medium ${status.type === 'error' ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-green-500/10 text-green-400 border border-green-500/20'}`}>
              {status.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
              {status.msg}
            </div>
          )}

          <form className="space-y-4" onSubmit={handlePasswordChange}>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-widest">Current Password</label>
              <input 
                type="password" 
                value={passwords.current}
                onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                placeholder="••••••••" 
                className="w-full bg-[#0b1221] border border-gray-700 rounded-lg px-4 py-3 mt-1 text-white outline-none focus:border-blue-500 transition-colors" 
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-widest">New Password</label>
              <input 
                type="password" 
                value={passwords.new}
                onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                placeholder="••••••••" 
                className="w-full bg-[#0b1221] border border-gray-700 rounded-lg px-4 py-3 mt-1 text-white outline-none focus:border-blue-500 transition-colors" 
              />
            </div>
            <button 
              type="submit" 
              disabled={isUpdating}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg mt-2 transition-colors disabled:opacity-50"
            >
              {isUpdating ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        <div className="space-y-6">
          {/* 2FA Card */}
          <div className="bg-[#131b2c] border border-gray-800 rounded-2xl p-6 shadow-xl flex items-start gap-4">
            <ShieldAlert className="text-purple-500 shrink-0" size={28} />
            <div>
              <h3 className="text-lg font-bold text-white mb-1">Two-Factor Auth (2FA)</h3>
              <p className="text-sm text-gray-400 mb-4">Add an extra layer of security to your account using an authenticator app.</p>
              <button className="bg-[#1e222d] border border-gray-700 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-800 transition-colors">Enable 2FA</button>
            </div>
          </div>

          {/* Active Sessions */}
          <div className="bg-[#131b2c] border border-gray-800 rounded-2xl p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <Smartphone className="text-gray-400" size={20} />
              <h3 className="text-white font-bold">Active Sessions</h3>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0b1221] rounded-lg border border-gray-800">
              <div>
                <p className="text-white text-sm font-bold">Windows • Chrome</p>
                <p className="text-xs text-green-500">Current Session (Noida, India)</p>
              </div>
              <button className="text-xs text-red-400 hover:underline">Log out</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Security;